/**
 * Die Rechnung hinter dem Mitscrollen – bewusst ohne DOM, damit sie sich
 * prüfen lässt.
 *
 * Quelltext und Vorschau sind zwei Achsen, die an bekannten Punkten
 * zusammenfallen. Jeder Block, den der Renderer mit seiner Quellzeile
 * beschriftet hat, liefert zwei solche Punkte: Anfang und Ende. Das Ende ist
 * der entscheidende Teil – ein Bild ist eine Zeile Quelltext und
 * vierhundert Pixel Vorschau. Zwischen zwei Punkten wird linear umgerechnet.
 */

/** Ein Block der Vorschau mit seiner Lage auf beiden Achsen. */
export interface Block {
  srcTop: number;
  srcBottom: number;
  viewTop: number;
  viewBottom: number;
}

/** Ein Punkt, an dem beide Achsen zusammenfallen. */
export interface Point {
  src: number;
  view: number;
}

export function buildPoints(blocks: readonly Block[], srcMax: number, viewMax: number): Point[] {
  const points: Point[] = [{ src: 0, view: 0 }];
  const add = (src: number, view: number) => {
    const last = points[points.length - 1];
    // Streng steigend halten: gleiche oder rückläufige Werte machen die
    // Umrechnung mehrdeutig und lassen die Vorschau springen.
    if (src > last.src && view > last.view) points.push({ src, view });
  };

  for (const block of blocks) {
    add(block.srcTop, block.viewTop);
    add(block.srcBottom, block.viewBottom);
  }

  const last = points[points.length - 1];
  if (srcMax > last.src && viewMax > last.view) points.push({ src: srcMax, view: viewMax });
  return points;
}

/** Scrollwert von einer Achse auf die andere. `forward` = Quelltext -> Vorschau. */
export function project(points: readonly Point[], value: number, forward: boolean): number {
  if (points.length < 2) return value;
  const from = (p: Point) => (forward ? p.src : p.view);
  const to = (p: Point) => (forward ? p.view : p.src);

  if (value <= from(points[0])) return to(points[0]);
  for (let i = 1; i < points.length; i++) {
    if (value > from(points[i])) continue;
    const span = from(points[i]) - from(points[i - 1]);
    if (span <= 0) return to(points[i]);
    const t = (value - from(points[i - 1])) / span;
    return to(points[i - 1]) + t * (to(points[i]) - to(points[i - 1]));
  }
  return to(points[points.length - 1]);
}

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}
